import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Overlay from "../../components/Overlay";

/*
 * Long edge of the encoded photo. Big enough to read a scratch or a scuff on a
 * dispute, small enough (~250-450 KB at q0.85) that four of them upload over
 * hotel wifi while the guest is still handing over the keys.
 */
const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

const useStyles = makeStyles({
  /* Camera capture modal */
  cameraCard: { width: "100%", maxWidth: 720, borderRadius: 12, border: `1px solid ${T.border}`, background: T.elevated, overflow: "hidden" },
  stage: { position: "relative", width: "100%", aspectRatio: "4 / 3", background: "#111", overflow: "hidden" },
  media: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" },
  stageMessage: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center", color: "rgba(255,255,255,0.85)", fontSize: 14 },
  body: { padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  bodyText: { minWidth: 0 },
  title: { margin: "2px 0 0", fontSize: 18, fontWeight: 600, color: T.fg },
  hint: { margin: "2px 0 0", fontSize: 12, color: T.mutedFg },
  actions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  hiddenInput: { display: "none" },
});

/** Downscale to MAX_EDGE_PX and encode JPEG. Source is a video frame or an image. */
export function encodeFrame(source, sourceWidth, sourceHeight) {
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(source, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve({ blob, width, height }) : reject(new Error("ENCODE_FAILED")),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/*
 * Decode a picked file so the fallback path produces the same normalised JPEG as
 * the camera path. This is also what makes an iPhone HEIC a non-issue, and
 * imageOrientation keeps an EXIF-rotated JPEG from coming out sideways.
 */
export async function encodeFile(file) {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    try {
      return await encodeFrame(bitmap, bitmap.width, bitmap.height);
    } finally {
      bitmap.close?.();
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("DECODE_FAILED"));
      el.src = url;
    });
    return await encodeFrame(img, img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/*
 * Live camera capture for one vehicle angle: preview -> shutter -> keep or
 * retake. Confirms with a JPEG blob; the caller owns the upload.
 *
 * Layered at z 1600 so it clears the intake overlay (1300), the toast (1400) and
 * the plate audit modal (1500).
 */
function CameraCaptureModal({ label, onCancel, onConfirm }) {
  const s = useStyles();
  const ui = useUi();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  /* "starting" | "live" | "shot" | "fallback" */
  const [mode, setMode] = useState("starting");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shot, setShot] = useState(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    /* Not a hard block: the concierge always gets the file-input fallback. */
    if (!navigator.mediaDevices?.getUserMedia) {
      setMode("fallback");
      setError(
        window.isSecureContext === false
          ? "The camera needs a secure (https) connection on this device. Pick a photo instead."
          : "This device has no camera available. Pick a photo instead.",
      );
      return undefined;
    }

    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({
        /* ideal, not exact: exact facingMode throws on a laptop with only a
         * front camera, which is every dev machine. */
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      })
      .then((stream) => {
        /* Resolved after unmount - stop it now or the camera LED stays on. */
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        setMode("live");
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((e) => {
        if (cancelled) return;
        setMode("fallback");
        setError(
          e?.name === "NotAllowedError" || e?.name === "SecurityError"
            ? "Camera access is blocked for this site. Allow it in the browser, or pick a photo instead."
            : e?.name === "NotFoundError" || e?.name === "OverconstrainedError"
            ? "No usable camera was found. Pick a photo instead."
            : "The camera could not be started. Pick a photo instead.",
        );
      });

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [stopStream]);

  /* Revoke the still preview when it is replaced or the modal closes. */
  useEffect(
    () => () => {
      if (shot?.url) URL.revokeObjectURL(shot.url);
    },
    [shot],
  );

  const takeShot = async () => {
    const video = videoRef.current;
    if (!video || busy) return;

    setBusy(true);
    try {
      /* videoWidth, not clientWidth: the element is object-fit cropped. */
      const encoded = await encodeFrame(
        video,
        video.videoWidth,
        video.videoHeight,
      );
      setShot({ ...encoded, url: URL.createObjectURL(encoded.blob) });
      setMode("shot");
    } catch (e) {
      setError("The photo could not be captured. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    /* The stream is still live, so retake is instant. */
    setShot(null);
    setMode(streamRef.current ? "live" : "fallback");
  };

  const confirm = () => {
    if (!shot) return;
    stopStream();
    onConfirm(shot.blob, { width: shot.width, height: shot.height });
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setBusy(true);
    try {
      const encoded = await encodeFile(file);
      setShot({ ...encoded, url: URL.createObjectURL(encoded.blob) });
      setMode("shot");
      setError(null);
    } catch (err) {
      setError(
        "That image could not be read. Use a JPEG or PNG, or try the camera.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onCancel} z={1600}>
      <div className={s.cameraCard}>
        <div className={s.stage}>
          {/* The video stays mounted behind the still: unmounting it would drop
              srcObject, and retake would then encode a blank frame. */}
          <video
            ref={videoRef}
            className={s.media}
            /* all three are required or iOS Safari shows a black frame */
            autoPlay
            muted
            playsInline
            onLoadedMetadata={() => setReady(true)}
          />

          {mode === "shot" && (
            <img src={shot.url} alt={`${label} photo`} className={s.media} />
          )}

          {mode !== "shot" && (!ready || error) && (
            <div className={s.stageMessage}>
              {error || "Starting the camera..."}
              {error && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={ui.ghostBtn}
                >
                  Pick a photo
                </button>
              )}
            </div>
          )}
        </div>

        <div className={s.body}>
          <div className={s.bodyText}>
            <div className={ui.eyebrow}>Vehicle condition</div>
            <h3 className={s.title}>{label}</h3>
            <p className={s.hint}>
              {mode === "shot"
                ? "Check the damage is visible before you keep it."
                : "Fill the frame with this side of the car."}
            </p>
          </div>

          <div className={s.actions}>
            <button onClick={onCancel} className={ui.mutedBtn}>
              Cancel
            </button>
            {mode === "shot" ? (
              <>
                <button onClick={retake} className={ui.ghostBtn}>
                  Retake
                </button>
                <button onClick={confirm} className={ui.primaryBtn}>
                  Use photo
                </button>
              </>
            ) : (
              <button
                onClick={takeShot}
                /* videoWidth is 0 until loadedmetadata, so a shot before then
                 * would encode an empty frame */
                disabled={mode !== "live" || !ready || busy}
                className={ui.primaryBtn}
              >
                {busy ? "Capturing..." : "Take photo"}
              </button>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={pickFile}
          className={s.hiddenInput}
        />
      </div>
    </Overlay>
  );
}

export default CameraCaptureModal;
